import type { SVGProps } from 'react';
import {
  Activity, AlertCircle, AlertTriangle, AlignLeft, AppWindow, ArrowLeft, ArrowRight, ArrowUp, ArrowUpRight,
  Award, BadgeCheck, Bell, Bookmark, BookOpen, Brain, Briefcase, Building, Building2,
  Calculator, Calendar, CalendarCheck, CalendarClock, CalendarPlus, CalendarRange, CalendarX,
  Check, CheckCheck, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ChevronsUpDown,
  Circle, CircleAlert, CircleCheck, CircleDot, CircleMinus, CircleX,
  ClipboardCheck, Clock, ClockAlert, Cloud, Code2, Coins, Columns3, Compass,
  Database, DoorOpen, Dot, Download, Dumbbell, ExternalLink, Eye, EyeOff,
  File as FileIcon, FileArchive, FileAudio, FileBarChart, FileCheck2, FileClock, FileImage, FileLock2, FilePlus,
  FileSignature, FileSpreadsheet, FileText, FileUp, FileVideo,
  Filter, Flag, FolderCheck, FolderGit2, Github, GraduationCap, Handshake, HardHat, Hash, HeartPulse, House,
  IdCard, Inbox, IndianRupee, Info, Kanban, Landmark, Languages, Layers, LayoutDashboard, Library, LifeBuoy, Lightbulb,
  ListChecks, Loader2, Lock, LogIn, LogOut,
  Mail, MailCheck, MailOpen, MapPin, Menu, MessageCircle, MessageCircleQuestion, MessageSquare, MessageSquareText,
  Mic, Minus, Moon, MoreHorizontal, Network, Palette, Paperclip, Pencil, Phone, Play, Plus,
  Radar, Receipt, RefreshCw, Repeat, Rocket, RotateCcw, RotateCw,
  ScanLine, Scale, School, ScrollText, Search, Send, Server, Settings2, Shield, ShieldAlert, ShieldCheck, Siren, Sparkles,
  Sun, SunMedium, Table2, Tags, Target, TextCursorInput, ToggleLeft, ToggleRight,
  Trash2, TrendingUp, TriangleAlert, Trophy, Type, Undo2,
  Upload, User, UserCheck, UserCog, UserPlus, UserRound, UserSearch, UserX, Users,
  Wallet, Workflow, Wrench, X, XCircle, Zap, ZapOff,
  type LucideIcon,
} from 'lucide-react';

/**
 * Whitelist map of lucide icons in use across the app. Add a name here
 * when a new site needs it; consumers import via `<Icon name="…" />` from
 * `@kollez/design-system` instead of `lucide-react` directly. This keeps
 * the icon system single-sourced and makes a future set-swap tractable.
 */
export const ICON_REGISTRY = {
  activity: Activity, 'alert-circle': AlertCircle, 'alert-triangle': AlertTriangle, 'align-left': AlignLeft,
  'app-window': AppWindow, 'arrow-left': ArrowLeft, 'arrow-right': ArrowRight, 'arrow-up': ArrowUp,
  'arrow-up-right': ArrowUpRight, award: Award,
  'badge-check': BadgeCheck, bell: Bell, bookmark: Bookmark, 'book-open': BookOpen, brain: Brain,
  briefcase: Briefcase, building: Building, 'building-2': Building2,
  calculator: Calculator, calendar: Calendar, 'calendar-check': CalendarCheck, 'calendar-clock': CalendarClock,
  'calendar-plus': CalendarPlus, 'calendar-range': CalendarRange, 'calendar-x': CalendarX,
  check: Check, 'check-check': CheckCheck, 'check-circle-2': CheckCircle2,
  'chevron-down': ChevronDown, 'chevron-left': ChevronLeft, 'chevron-right': ChevronRight, 'chevron-up': ChevronUp,
  'chevrons-up-down': ChevronsUpDown,
  circle: Circle, 'circle-alert': CircleAlert, 'circle-check': CircleCheck, 'circle-dot': CircleDot,
  'circle-minus': CircleMinus, 'circle-x': CircleX,
  'clipboard-check': ClipboardCheck, clock: Clock, 'clock-alert': ClockAlert, cloud: Cloud, 'code-2': Code2,
  coins: Coins, 'columns-3': Columns3, compass: Compass,
  database: Database, 'door-open': DoorOpen, dot: Dot, download: Download, dumbbell: Dumbbell,
  'external-link': ExternalLink, eye: Eye, 'eye-off': EyeOff,
  file: FileIcon, 'file-archive': FileArchive, 'file-audio': FileAudio, 'file-bar-chart': FileBarChart,
  'file-check-2': FileCheck2, 'file-clock': FileClock, 'file-image': FileImage, 'file-lock-2': FileLock2,
  'file-plus': FilePlus, 'file-signature': FileSignature, 'file-spreadsheet': FileSpreadsheet, 'file-text': FileText,
  'file-up': FileUp, 'file-video': FileVideo,
  filter: Filter, flag: Flag, 'folder-check': FolderCheck, 'folder-git-2': FolderGit2,
  github: Github, 'graduation-cap': GraduationCap, handshake: Handshake, 'hard-hat': HardHat, hash: Hash,
  'heart-pulse': HeartPulse, house: House,
  'id-card': IdCard, inbox: Inbox, 'indian-rupee': IndianRupee, info: Info, kanban: Kanban, landmark: Landmark,
  languages: Languages, layers: Layers, 'layout-dashboard': LayoutDashboard, library: Library, 'life-buoy': LifeBuoy,
  lightbulb: Lightbulb,
  'list-checks': ListChecks, 'loader-2': Loader2, lock: Lock, 'log-in': LogIn, 'log-out': LogOut,
  mail: Mail, 'mail-check': MailCheck, 'mail-open': MailOpen, 'map-pin': MapPin, menu: Menu,
  'message-circle': MessageCircle, 'message-circle-question': MessageCircleQuestion, 'message-square': MessageSquare,
  'message-square-text': MessageSquareText,
  mic: Mic, minus: Minus, moon: Moon, 'more-horizontal': MoreHorizontal, network: Network,
  palette: Palette, paperclip: Paperclip, pencil: Pencil, phone: Phone, play: Play, plus: Plus,
  radar: Radar, receipt: Receipt, 'refresh-cw': RefreshCw, repeat: Repeat, rocket: Rocket,
  'rotate-ccw': RotateCcw, 'rotate-cw': RotateCw,
  'scan-line': ScanLine, scale: Scale, school: School, 'scroll-text': ScrollText,
  search: Search, send: Send, server: Server, 'settings-2': Settings2,
  shield: Shield, 'shield-alert': ShieldAlert, 'shield-check': ShieldCheck, siren: Siren, sparkles: Sparkles,
  sun: Sun, 'sun-medium': SunMedium, 'table-2': Table2, tags: Tags, target: Target, 'text-cursor-input': TextCursorInput,
  'toggle-left': ToggleLeft, 'toggle-right': ToggleRight,
  'trash-2': Trash2, 'trending-up': TrendingUp, 'triangle-alert': TriangleAlert, trophy: Trophy, type: Type, 'undo-2': Undo2,
  upload: Upload, user: User, 'user-check': UserCheck, 'user-cog': UserCog, 'user-plus': UserPlus,
  'user-round': UserRound, 'user-search': UserSearch, 'user-x': UserX, users: Users,
  wallet: Wallet, workflow: Workflow, wrench: Wrench, x: X, 'x-circle': XCircle, zap: Zap, 'zap-off': ZapOff,
} as const satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICON_REGISTRY;

export type IconTone =
  | 'primary' | 'secondary' | 'tertiary' | 'accent'
  | 'success' | 'warning' | 'danger' | 'info' | 'inverse';

const TONE_COLOR: Record<IconTone, string> = {
  primary: 'rgb(var(--ink-primary))',
  secondary: 'rgb(var(--ink-secondary))',
  tertiary: 'rgb(var(--ink-tertiary))',
  accent: 'rgb(var(--ink-accent))',
  success: 'rgb(var(--ink-success))',
  warning: 'rgb(var(--ink-warning))',
  danger: 'rgb(var(--ink-danger))',
  info: 'rgb(var(--ink-info))',
  inverse: 'rgb(var(--ink-inverse))',
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  /** Pixel size (matches lucide's own API). Defaults to 16. */
  size?: number;
  /** Semantic tone keyed to --ink-* tokens. Defers to explicit `color` if both are set. */
  tone?: IconTone;
}

/**
 * Kollez icon primitive. Wraps the lucide icon registry so consumers
 * import a single `<Icon name="…" />` from `@kollez/design-system`
 * instead of `lucide-react` directly.
 *
 * ```tsx
 * <Icon name="bell" size={16} tone="tertiary" />
 * ```
 */
export function Icon({ name, size = 16, tone, color, ...rest }: IconProps) {
  const Cmp = ICON_REGISTRY[name];
  const resolvedColor = color ?? (tone ? TONE_COLOR[tone] : undefined);
  return (
    <Cmp
      size={size}
      color={resolvedColor}
      aria-hidden={rest['aria-label'] ? undefined : true}
      {...rest}
    />
  );
}
